import React, { useEffect, useState } from "react";
import { Download, Check } from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";

/**
 * InstallPWAButton — capture l'event beforeinstallprompt (Chrome/Edge/Android),
 * et affiche un bouton permettant d'installer le back-office sur le device.
 *
 * - Sur desktop Chrome/Edge : ouvre la boîte de dialogue native.
 * - Sur iOS Safari : affiche un toast avec instructions (Partage → Sur l'écran d'accueil).
 * - Reste masqué si déjà installée (display-mode: standalone).
 *
 * Props:
 *   variant: "compact" (icône seule, pour topbar) | "full" (texte + icône)
 */
export default function InstallPWAButton({ variant = "compact", className = "" }) {
  const { settings } = useSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Déjà installée ?
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success("Application installée sur votre appareil");
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);

  const install = async () => {
    if (isIOS) {
      toast.info(
        "Sur iPhone/iPad : appuyez sur l'icône Partager en bas, puis « Sur l'écran d'accueil » pour installer le raccourci.",
        { duration: 8000 }
      );
      return;
    }
    if (!deferredPrompt) {
      toast.info("Installation non disponible sur ce navigateur — essayez Chrome ou Edge.");
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome !== "accepted") {
      toast("Installation annulée");
    }
    setDeferredPrompt(null);
  };

  if (installed) {
    return variant === "compact" ? (
      <div className={`w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center ${className}`} title="Installée" aria-label="Application installée">
        <Check className="w-4 h-4" />
      </div>
    ) : null;
  }

  // Rien à afficher si aucune possibilité (browser desktop non supporté)
  if (!deferredPrompt && !isIOS) return null;

  if (variant === "compact") {
    return (
      <button
        type="button"
        onClick={install}
        className={`w-9 h-9 rounded-lg hover:bg-white/10 text-white flex items-center justify-center transition ${className}`}
        aria-label={`Installer ${settings?.company_name || "l'application"} sur cet appareil`}
        title="Installer sur cet appareil"
        data-testid="install-pwa-btn"
      >
        <Download className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={install}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-navy)] text-white font-semibold text-sm transition shadow-sm ${className}`}
      data-testid="install-pwa-btn-full"
    >
      <Download className="w-4 h-4" />Installer l'application
    </button>
  );
}
